// src/Payroll_Management/PayRoll_Report/EmployeeTransferReportPrint.jsx
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Printer, ArrowLeft } from "lucide-react";

const BASE_URL = "https://nissi-india.com"; // change if needed

function s(v) {
  return (v ?? "").toString().trim();
}
function getQuery(locationSearch) {
  const p = new URLSearchParams(locationSearch);
  return { empId: s(p.get("empId")) };
}

// format numbers without crashing
function fmtNum(v) {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  // keep as plain (no decimals forced)
  return n.toString();
}

// SP returns: "~\Employee_photo\abc.jpg"
function normalizePhotoUrl(dbPath) {
  const raw = s(dbPath);
  if (!raw) return "/noimage.jpg";

  // remove "~"
  let p = raw.replace(/^~/, "");

  // replace backslashes with forward slashes
  p = p.replaceAll("\\", "/");

  // ensure leading slash
  if (!p.startsWith("/")) p = "/" + p;

  return p; // e.g. "/Employee_photo/abc.jpg"
}

export default function EmployeeTransferReportPrint() {
  const location = useLocation();
  const navigate = useNavigate();
  const { empId } = useMemo(() => getQuery(location.search), [location.search]);

  const finYearId = localStorage.getItem("finYearId") || "";

  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState(null);

  // company header text (same as your screenshot)
  const company = useMemo(
    () => ({
      title: "Nissi Infotech",
      sub: "Nissi",
      line1: "CIVIL CONSTRUCTION",
      address:
        "3/42-12, Nagappa colony, kavundampalayam, Coimbatore - 641030. Tamil Nadu. India.",
      phone: "Phone No : 9003744644",
      web: "infraconsoft-implementation.nissiinfotech.in",
    }),
    []
  );

  const fetchData = async () => {
    if (!String(finYearId).trim()) {
      alert("Financial Year not selected.");
      return;
    }
    if (!empId) {
      alert("Missing empId in URL. Example: ?empId=2635");
      return;
    }

    try {
      setLoading(true);

      // Uses your Node endpoint:
      // GET /api/employee-transfer-report/print-one?finYearId=..&empId=..
      const res = await axios.get(`${BASE_URL}/api/employee-transfer-report/print-one`, {
        params: {
          finYearId: Number(finYearId),
          empId: String(empId),
        },
      });

      if (res?.data?.success) {
        // Your Node returns { header: {...} }
        setRow(res.data.header || null);
      } else {
        alert(res?.data?.message || "Failed to load print data");
      }
    } catch (err) {
      alert(err?.response?.data?.message || err?.message || "Print data fetch failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPrint = () => window.print();

  // Map from SP columns (exact)
  const branchName = row ? s(row.frombranch) : "";
  const reportDate = row ? s(row.trdate) : "";

  const photoSrc = row ? normalizePhotoUrl(row.fld_photo) : "";

  const detailLines = row
    ? [
        ["EMPLOYEE CODE", s(row.code)],
        ["EMPLOYEE NAME", s(row.empname)],
        ["DESIGNATION", s(row.designme)],
        ["TRANSFERRED TO", s(row.brnme)],
        ["TRANSFERRED EFFECTIVE DATE", s(row.effectdate)],
        ["MONTHLY SALARY", fmtNum(row.fld_grosssalary)],
        ["DAILY BATTA", fmtNum(row.fld_bata)],
        ["HRA", fmtNum(row.fld_SpecialHRA)],
        ["SALARY ADVANCE", fmtNum(row.fld_balanceamount)],
        ["CANTEEN DUE", fmtNum(row.canteen)],
        ["BATTA PAID UPTO", s(row.paidupto)],
        ["TRAVELLING ADVANCE", fmtNum(row.travel)],
        ["REASON FOR TRANSFER", s(row.fld_reasonfor)],
      ]
    : [];

  // Period table (exact from SP)
  const periodFrom = row ? s(row.fld_wrkdperiodfrom) : "";
  const periodTo = row ? s(row.fld_wrkdperiodto) : "";
  const totalLeave = row ? fmtNum(row.fld_leavedays) : "";
  const lossOfPay = row ? fmtNum(row.fld_lopdays) : "";

  // footer time: use SP created time if available
  const printedOn = row ? s(row.fld_created_on) : "";

  return (
    <div style={{ background: "#e5e7eb", minHeight: "100vh", padding: 16 }}>
      {/* Toolbar (screen only) */}
      <div className="no-print" style={{ maxWidth: 860, margin: "0 auto 12px auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10 }}>
          <button style={btnLight()} onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
            Back
          </button>

          <button style={btnDark()} onClick={onPrint} disabled={loading}>
            <Printer size={16} />
            Print
          </button>
        </div>
      </div>

      {/* Print Area */}
      <div
        id="print-area"
        style={{
          maxWidth: 860,
          margin: "0 auto",
          background: "white",
          border: "1px solid #111",
          padding: 14,
        }}
      >
        {/* Outer frame */}
        <div style={{ border: "1px solid #111", padding: 14 }}>
          {/* Header */}
          <div style={{ border: "1px solid #111", padding: 10 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
              {/* Logo */}
              <div style={logoBox()}>LOGO</div>

              {/* Company info */}
              <div style={{ flex: 1, textAlign: "center" }}>
                <div style={{ fontSize: 16, fontWeight: 900 }}>{company.title}</div>
                <div style={{ fontSize: 12 }}>{company.sub}</div>
                <div style={{ fontSize: 13, fontWeight: 900, marginTop: 2 }}>{company.line1}</div>
                <div style={{ fontSize: 10, marginTop: 4 }}>{company.address}</div>
                <div style={{ fontSize: 10, marginTop: 2 }}>
                  {company.phone} &nbsp;&nbsp; {company.web}
                </div>
              </div>

              <div style={{ width: 92 }} />
            </div>
          </div>

          {/* Title bar */}
          <div
            style={{
              borderLeft: "1px solid #111",
              borderRight: "1px solid #111",
              borderBottom: "1px solid #111",
              padding: "8px 10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              fontWeight: 900,
              fontSize: 12,
            }}
          >
            <div>EMPLOYEE TRANSFER REPORT- {branchName ? branchName.toUpperCase() : ""}</div>
            <div>DATE : {reportDate}</div>
          </div>

          {/* Detail section */}
          <div
            style={{
              borderLeft: "1px solid #111",
              borderRight: "1px solid #111",
              borderBottom: "1px solid #111",
              padding: 10,
              minHeight: 360,
            }}
          >
            <div style={{ display: "flex", gap: 16 }}>
              {/* Left details */}
              <div style={{ flex: 1 }}>
                {loading ? (
                  <div style={{ fontSize: 12, padding: 6 }}>Loading...</div>
                ) : !row ? (
                  <div style={{ fontSize: 12, padding: 6 }}>No data</div>
                ) : (
                  detailLines.map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "190px 14px 1fr",
                        gap: 8,
                        padding: "6px 0",
                      }}
                    >
                      <div style={{ fontSize: 11, fontWeight: 900 }}>{label}</div>
                      <div style={{ fontSize: 11, fontWeight: 900 }}>:</div>
                      <div style={{ fontSize: 11, whiteSpace: "pre-wrap" }}>{value}</div>
                    </div>
                  ))
                )}
              </div>

              {/* Right photo box */}
              <div style={{ width: 150, display: "flex", justifyContent: "flex-end" }}>
                <div
                  style={{
                    width: 120,
                    height: 140,
                    border: "2px solid #2b6cb0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    background: "#fff",
                  }}
                >
                  {photoSrc ? (
                    <img
                      src={photoSrc}
                      alt="Employee"
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = "/noimage.jpg";
                      }}
                    />
                  ) : (
                    <div style={{ color: "#f97316", fontWeight: 900, fontSize: 14, textAlign: "center" }}>
                      No Image
                      <br />
                      Help!
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Period table */}
          <div style={{ borderLeft: "1px solid #111", borderRight: "1px solid #111", borderBottom: "1px solid #111" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={th()}>PERIOD</th>
                  <th style={th()}>TOTAL LEAVE</th>
                  <th style={th()}>LOSS OF PAY</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td style={td("center")}>
                    {periodFrom} {periodTo ? ` to ${periodTo}` : ""}
                  </td>
                  <td style={td("center")}>{totalLeave}</td>
                  <td style={td("center")}>{lossOfPay}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer sign row */}
          <div style={{ borderLeft: "1px solid #111", borderRight: "1px solid #111", borderBottom: "1px solid #111", padding: 16 }}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr 1fr",
                gap: 10,
                textAlign: "center",
                fontSize: 11,
                fontWeight: 900,
              }}
            >
              <div>Prepared By</div>
              <div>Checked By</div>
              <div>Authorized Signatory</div>
            </div>

            <div style={{ marginTop: 18, fontSize: 10 }}>{printedOn}</div>
          </div>
        </div>
      </div>

      {/* Print CSS */}
      <style>{`
        @page { size: A4; margin: 10mm; }
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
          #print-area {
            max-width: none !important;
            margin: 0 !important;
            border: none !important;
            padding: 0 !important;
          }
        }
      `}</style>
    </div>
  );
}

/* styles */
function btnLight() {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 14,
    border: "1px solid #d1d5db",
    background: "white",
    fontWeight: 800,
    cursor: "pointer",
  };
}
function btnDark() {
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 14px",
    borderRadius: 14,
    border: "1px solid #111827",
    background: "#111827",
    color: "white",
    fontWeight: 900,
    cursor: "pointer",
  };
}
function logoBox() {
  return {
    width: 92,
    height: 72,
    border: "1px solid #111",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 900,
    fontSize: 12,
  };
}
function th() {
  return {
    border: "1px solid #111",
    padding: "6px 6px",
    fontSize: 11,
    fontWeight: 900,
    textAlign: "center",
    background: "#f3f4f6",
  };
}
function td(align = "left") {
  return {
    border: "1px solid #111",
    padding: "6px 6px",
    fontSize: 11,
    textAlign: align,
    verticalAlign: "top",
    whiteSpace: "nowrap",
  };
}
